import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Image,
  Form,
  Button,
  CardHeader,
  CardTitle,
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import InputField from "../../../components/input/input-field";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import { useDispatch } from "react-redux";
import {
  fetchPermissionsData,
  fetchRolesData,
} from "../../../store/setting/actions";

const SignIn = () => {
  const alertRef = useRef(null);
  const location = useLocation();
  const [status, setStatus] = useState(location.state?.status || "");
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "expired") {
      setAlert({
        message: "Your session is expired, please login again",
        type: "danger",
        show: true,
        alertFor: "signin",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } else if (status === "invalid-token") {
      setAlert({
        message: "Invalid token. Please login again",
        type: "danger",
        show: true,
        alertFor: "signin",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  }, [status]);

  useEffect(() => {
    if (location.state?.status) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    setIsLoading(true);
    try {
      const response = await axios.post(`${url.backendURL}admin/login`, {
        email: values.email,
        password: values.password,
      });
      localStorage.setItem("coyote-token", response.data.response.jwt);
      setAlert({ show: false, alertFor: "", message: "", type: "" });
      dispatch(fetchPermissionsData());
      dispatch(fetchRolesData());
      navigate("/dashboard");
    } catch (err) {
      const { response } = err;
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor: "signin",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .required("Email is required")
      .email("Enter a valid email address")
      .test(
        "no-spaces",
        "Email may not contain any spaces at the beginning or end",
        (value) => !value || value.trim() === value
      )
      .test("min-domain-segments", "Enter a valid email address", (value) => {
        if (!value) return true;
        const parts = value.split("@");
        if (parts.length !== 2) return false;
        const domainSegments = parts[1].split(".");
        return (
          domainSegments.length >= 2 &&
          domainSegments.every((s) => s.length >= 2)
        );
      })
      .test("realistic-email", "Enter a valid email address", (value) => {
        if (!value) return true;
        const [local, domain] = value.split("@");
        if (!local || !domain) return false;

        if (/^\d+$/.test(local)) return false;
        if (local.length > 64) return false;

        if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) return false;

        return true;
      }),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters long")
      .max(30, "Password cannot be longer than 30 characters")
      .matches(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\S]{6,}$/,
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
      )
      .required("Password is required"),
  });

  return (
    <section className="login-content admin-login-content vh-100">
      <Row className="admin-row h-100 m-0 justify-content-center align-items-center w-100">
        <Col>
          <Row
            className="position-relative h-100 align-items-center justify-content-center gap-4"
            style={{
              zIndex: 2,
            }}
          >
            <Col className="h-100" md="10" ref={alertRef}>
              <Card className="shadow-sm px-3 py-4">
                <CardHeader className="space-y-1">
                  <CardTitle className="fs-1 text-center font-bold mb-2">
                    Admin Sign In
                  </CardTitle>
                </CardHeader>
                <AlertNotificationSimple
                  type={alert.type}
                  message={alert.message}
                  show={alert.show}
                  onClose={() => setAlert({ ...alert, show: false })}
                />

                <Formik
                  initialValues={{
                    email: "",
                    password: "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit} className="pt-2">
                      <FocusError />
                      <Row>
                        <Col lg="12" style={{ zIndex: "1" }}>
                          <InputField
                            className="form-group"
                            type="email"
                            name="email"
                            placeholder="example@email.com"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            asterick={true}
                            errorMessage={
                              alert?.message ===
                              "Email is not allowed to be empty"
                                ? [alert.message]
                                : []
                            }
                            label="Email"
                          />
                        </Col>
                        <Col lg="12">
                          <InputField
                            className="form-group"
                            type="password"
                            name="password"
                            placeholder="Enter your password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            showPasswordToggle={true}
                            showPassword={showPassword}
                            setShowPassword={setShowPassword}
                            asterick={true}
                            errorMessage={
                              alert?.message ===
                              "Password is not allowed to be empty"
                                ? [alert.message]
                                : []
                            }
                            label="Password"
                          />
                        </Col>
                        <Col lg="12" className="d-flex justify-content-between">
                          <Form.Check className="form-check mb-3">
                            <Form.Check.Input
                              type="checkbox"
                              id="customCheck1"
                            />
                            <Form.Check.Label htmlFor="customCheck1">
                              Remember Me
                            </Form.Check.Label>
                          </Form.Check>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-center">
                        <Button
                          type="submit"
                          variant="btn btn-primary"
                          size="md"
                          disabled={isLoading}
                        >
                          {isLoading ? "Signing In..." : "Sign In"}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </Card>
            </Col>
          </Row>
          <div
            className="sign-bg"
            style={{
              zIndex: 1,
            }}
          >
            <svg
              width="280"
              height="230"
              viewBox="0 0 431 398"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.05">
                <rect
                  x="-157.085"
                  y="193.773"
                  width="543"
                  height="77.5714"
                  rx="38.7857"
                  transform="rotate(-45 -157.085 193.773)"
                  fill="#3B8AFF"
                />
                <rect
                  x="7.46875"
                  y="358.327"
                  width="543"
                  height="77.5714"
                  rx="38.7857"
                  transform="rotate(-45 7.46875 358.327)"
                  fill="#3B8AFF"
                />
                <rect
                  x="61.9355"
                  y="138.545"
                  width="310.286"
                  height="77.5714"
                  rx="38.7857"
                  transform="rotate(45 61.9355 138.545)"
                  fill="#3B8AFF"
                />
                <rect
                  x="62.3154"
                  y="-190.173"
                  width="543"
                  height="77.5714"
                  rx="38.7857"
                  transform="rotate(45 62.3154 -190.173)"
                  fill="#3B8AFF"
                />
              </g>
            </svg>
          </div>
        </Col>
      </Row>
    </section>
  );
};

export default SignIn;
